import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from '../../styles/GiftBox3D.module.css';

/**
 * GiftBox3D - Interactive 3D gift box component with perpetual wobble animation.
 * On click, fires onClick immediately (to start data fetching) and plays the
 * full shrink animation. When the animation ends, fires onAnimationComplete.
 *
 * @param {Function} onClick - Fires immediately on click (use to start API calls)
 * @param {Function} onAnimationComplete - Fires when the box finishes shrinking to 0
 * @param {boolean} disabled - Whether the gift box is disabled (e.g., during reveal)
 */
const GiftBox3D = ({ onClick, onAnimationComplete, disabled = false }) => {
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = () => {
    if (disabled || isClicked) return;

    setIsClicked(true);

    // Fire onClick immediately so the parent can start the API call
    // while the box animation plays
    if (onClick) {
      onClick();
    }
  };

  // Called when the shrinkAndFade animation finishes on the wrapper
  const handleAnimationEnd = useCallback((e) => {
    // Only react to the shrinkAndFade animation on this element, not child animations
    if (e.target === e.currentTarget && onAnimationComplete) {
      onAnimationComplete();
    }
  }, [onAnimationComplete]);

  return (
    <button
      type="button"
      className={styles.scene}
      onClick={handleClick}
      disabled={disabled || isClicked}
      aria-label={disabled ? 'Revealing gift...' : 'Click to reveal your gift'}
    >
      <div className={styles.giftWrapper}>
        <div
          className={isClicked ? styles.clickedAnimation : undefined}
          onAnimationEnd={isClicked ? handleAnimationEnd : undefined}
        >
          <div className={isClicked ? styles.giftContainerWobbling : styles.giftContainer}>
          {/* Shadow */}
          <div className={styles.shadow}></div>

          {/* Box */}
          <div className={styles.box}>
            {/* Box faces */}
            <div className={`${styles.boxFace} ${styles.front}`}></div>
            <div className={`${styles.boxFace} ${styles.back}`}></div>
            <div className={`${styles.boxFace} ${styles.right}`}></div>
            <div className={`${styles.boxFace} ${styles.left}`}></div>
            <div className={`${styles.boxFace} ${styles.top}`}></div>
            <div className={`${styles.boxFace} ${styles.bottom}`}></div>

            {/* Ribbons */}
            <div className={`${styles.ribbon} ${styles.verticalFront}`}></div>
            <div className={`${styles.ribbon} ${styles.verticalBack}`}></div>
            <div className={`${styles.ribbon} ${styles.verticalRight}`}></div>
            <div className={`${styles.ribbon} ${styles.verticalLeft}`}></div>
            <div className={`${styles.ribbon} ${styles.horizontalFront}`}></div>
            <div className={`${styles.ribbon} ${styles.horizontalBack}`}></div>
            <div className={`${styles.ribbon} ${styles.horizontalRight}`}></div>
            <div className={`${styles.ribbon} ${styles.horizontalLeft}`}></div>
            <div className={`${styles.ribbon} ${styles.topVertical}`}></div>
            <div className={`${styles.ribbon} ${styles.topHorizontal}`}></div>
            <div className={`${styles.ribbon} ${styles.bottomVertical}`}></div>
            <div className={`${styles.ribbon} ${styles.bottomHorizontal}`}></div>
          </div>

          {/* Bow */}
          <div className={styles.bow}>
            <div className={`${styles.bowLoop} ${styles.left}`}></div>
            <div className={`${styles.bowLoop} ${styles.right}`}></div>
            <div className={styles.bowKnot}></div>
            <div className={`${styles.bowTail} ${styles.left}`}></div>
            <div className={`${styles.bowTail} ${styles.right}`}></div>
          </div>
          </div>
        </div>
      </div>
    </button>
  );
};

GiftBox3D.propTypes = {
  onClick: PropTypes.func.isRequired,
  onAnimationComplete: PropTypes.func,
  disabled: PropTypes.bool,
};

export default GiftBox3D;
